import { AxiosResponse } from 'axios';
import client from './clientRetryable';
import getRequestHeader from '@/util/getRequestHeader';
import IdDocType from '@/resources/enum/IdDocType';
import MnDocType from '@/resources/enum/MnDocType';
import RedirectMode from '@/resources/enum/RedirectMode';
import RegionCode from '@/resources/enum/RegionCode';
import { LocalesDictionary } from '@/store/modules/i18n';
import { checkCorrectLocales } from '@/util/checki18n';

export default {
  async fetch(): Promise<AxiosResponse<Response>> {
    const response: AxiosResponse<Response> = await client.post<Response>('auth', {}, { headers: getRequestHeader() });

    await checkCorrectLocales(response.data.kycOrgConfig.orgNames);
    await checkCorrectLocales(response.data.warningMessage);
    await checkCorrectLocales(response.data.errorSupplementaryMessage);
    await Promise.all(response.data.generalDocs?.map(value => checkCorrectLocales(value.generalDocName)) || []);
    await Promise.all(response.data.generalDocs?.map(value => checkCorrectLocales(value.description)) || []);

    // 補助書類の文言確認
    await Promise.all(
      response.data.optionalDocs?.map(value =>
        Promise.all(
          value.selection.map(optionalDoc =>
            Promise.all([
              checkCorrectLocales(optionalDoc.optionalDocName),
              checkCorrectLocales(optionalDoc.description)
            ])
          )
        )
      ) || []
    );

    return response;
  }
};

export interface Response {
  accessToken: string;
  idDocTypes: IdDocType[];
  kycOrgConfig: KycOrgConfig;
  kycRequestID: number;
  applicantID: number;
  kycRequestRedirectURL: string;
  kycRequestErrorRedirectURL: string;
  applicantCustomColors: ApplicantCustomColor[];
  warningMessage: LocalesDictionary;
  errorSupplementaryMessage: LocalesDictionary;
  requestTimes: number;
  myNumberDocTypes?: MnDocType[];
  myNumberCollectFlg: boolean;
  generalDocs?: GeneralDoc[];
  regionCode: RegionCode;
  defaultLanguage: 'ja' | 'en' | 'id' | '';
  optionalDocs?: OptionalDocs[];
}

export interface KycOrgConfig {
  id: number;
  orgNames: LocalesDictionary;
  birthAuth: boolean;
  redirectURL: string;
  redirectMode: RedirectMode;
  consignmentText: string;
  checkKYCRequestInformation: boolean;
  checkTermsOfService: boolean;
  agreedTermsOfService: boolean;
  autoShootIDDocTypes: IdDocType[];
  enableClientTypePC: boolean;
  enableShrinkFrame: boolean;
  useErrorRedirectURL: boolean;
}

export interface ApplicantCustomColor {
  key: string;
  colorCode: string;
}

export interface GeneralDoc {
  generalDocType: string;
  displayPriority: number;
  generalDocName: LocalesDictionary;
  description: LocalesDictionary;
  maxPages: number;
}

export interface OptionalDocs {
  seqNo: number;
  selection: {
    displayPriority: number;
    optionalDocType: string;
    optionalDocName: LocalesDictionary;
    description: LocalesDictionary;
    maxPages: number;
  }[];
}
